import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
} from '@mui/material';
import {
  Add,
  Clear,
  Delete,
  FilterList,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Sort,
  ViewColumn,
} from '@mui/icons-material';
import api, { handleApiError } from '../../services/api';
import useDebounce from '../../hooks/debounceHook';
import { useWorkspace } from '../../utils/WorkspaceContext';
import { fmtRange } from '../../utils/sprintSlots';
import CreateProjectDialog from '../dialogs/ProjectDialog';
import CreateTrackDialog from '../dialogs/TrackDialog';
import TaskCreate from '../KeyComponents/TaskCreate';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderRight: 'none',
  },
}));

const ClickableCell = styled(StyledTableCell)(({ theme }) => ({
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
  '&:last-child': {
    borderRight: 'none',
  },
}));

const EpicRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
}));

const ProjectRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TrackRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const SprintRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(0.1),
  padding: theme.spacing(0.1),
}));

const COL_SX = {
  epic: { width: 200, minWidth: 200, maxWidth: 200 },
  project: { width: 240, minWidth: 240, maxWidth: 240 },
  track: { width: 240, minWidth: 240, maxWidth: 240 },
  description: { width: 260, minWidth: 260, maxWidth: 260 },
  owner: { width: 200, minWidth: 200, maxWidth: 200 },
  sprint: { width: 200, minWidth: 200, maxWidth: 200 },
  goals: { width: 520, minWidth: 520, maxWidth: 520 },
  status: { width: 140, minWidth: 140, maxWidth: 140 },
  metric: { width: 160, minWidth: 160, maxWidth: 160 },
};

const TrackSprintDetail = () => {
  const { currentWorkspace } = useWorkspace();
  const storageKey = `trackSprintDetail_${currentWorkspace?.id}`;
  
  const [data, setData] = useState([]);
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);
  
  // Load from localStorage or use defaults
  const [expandedEpics, setExpandedEpics] = useState(() => {
    const saved = localStorage.getItem(`${storageKey}_expandedEpics`);
    return saved ? JSON.parse(saved) : {};
  });
  const [expandedProjects, setExpandedProjects] = useState(() => {
    const saved = localStorage.getItem(`${storageKey}_expandedProjects`);
    return saved ? JSON.parse(saved) : {};
  });
  const [expandedTracks, setExpandedTracks] = useState(() => {
    const saved = localStorage.getItem(`${storageKey}_expandedTracks`);
    return saved ? JSON.parse(saved) : {};
  });
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem(`${storageKey}_filters`);
    return saved ? JSON.parse(saved) : [];
  });
  const [sortConfig, setSortConfig] = useState(() => {
    const saved = localStorage.getItem(`${storageKey}_sortConfig`);
    return saved ? JSON.parse(saved) : { field: null, direction: 'asc' };
  });
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const defaults = {
      epic: true,
      project: true,
      track: true,
      description: true,
      owner: true,
      sprint: true,
      goals: true,
      status: true,
      tasksCompleted: true,
      totalTasks: true,
      timeTaken: true,
      estimatedTime: true,
      reviewRemaining: true,
    };
    const saved = localStorage.getItem(`${storageKey}_visibleColumns`);
    // Merge saved prefs over defaults so newly-added columns still appear.
    return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
  });
  
  // New filter state for building filters
  const [newFilter, setNewFilter] = useState({
    field: 'epic',
    operator: 'contains',
    value: '',
  });
  
  // State for new goal inputs (keyed by sprint ID)
  const [newGoalInputs, setNewGoalInputs] = useState({});
  
  // State for tracking which sprint is currently creating a goal
  const [creatingGoal, setCreatingGoal] = useState(null);
  
  // State for creating sprints (tracking which track is creating)
  const [creatingSprint, setCreatingSprint] = useState(null);
  
  // State for dialogs
  const [openProjectDialog, setOpenProjectDialog] = useState(false);
  const [openTrackDialog, setOpenTrackDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [taskPrefill, setTaskPrefill] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const normalizePayload = (payload) => {
    if (!Array.isArray(payload)) return [];
    return payload.map((epic) => ({
      ...epic,
      projects: epic.projects?.map((project) => ({
        ...project,
        tracks: project.tracks?.map((track) => ({
          ...track,
          sprints: track.sprints?.map((sprint) => ({
            ...sprint,
            sprint_goals: sprint.sprint_goals?.map((goal) => ({
              ...goal,
              is_completed: goal.is_completed ?? false,
            })),
          })),
        })),
      })),
    }));
  };

  useEffect(() => {
    const fetchTrackSprintData = async () => {
      try {
        setLoading(true);
        const response = await api.get('track-sprints-detail/', {
          params: { workspace_id: currentWorkspace.id },
        });
        setData(normalizePayload(response.data.payload));
      } catch (error) {
        console.error('Error fetching track sprint data:', handleApiError(error));
      } finally {
        setLoading(false);
      }
    };

    const fetchWorkspaceUsers = async () => {
      try {
        const response = await api.get('assignees/', {
          params: { workspace_id: currentWorkspace.id },
        });
        setWorkspaceUsers(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching workspace users:', handleApiError(error));
      }
    };

    if (currentWorkspace?.id) {
      fetchTrackSprintData();
      fetchWorkspaceUsers();
    }
  }, [currentWorkspace]);

  // Save to localStorage whenever state changes
  useEffect(() => {
    if (currentWorkspace?.id) {
      localStorage.setItem(`${storageKey}_expandedEpics`, JSON.stringify(expandedEpics));
    }
  }, [expandedEpics, storageKey, currentWorkspace]);

  useEffect(() => {
    if (currentWorkspace?.id) {
      localStorage.setItem(`${storageKey}_expandedProjects`, JSON.stringify(expandedProjects));
    }
  }, [expandedProjects, storageKey, currentWorkspace]);

  useEffect(() => {
    if (currentWorkspace?.id) {
      localStorage.setItem(`${storageKey}_expandedTracks`, JSON.stringify(expandedTracks));
    }
  }, [expandedTracks, storageKey, currentWorkspace]);

  useEffect(() => {
    if (currentWorkspace?.id) {
      localStorage.setItem(`${storageKey}_filters`, JSON.stringify(filters));
    }
  }, [filters, storageKey, currentWorkspace]);

  useEffect(() => {
    if (currentWorkspace?.id) {
      localStorage.setItem(`${storageKey}_sortConfig`, JSON.stringify(sortConfig));
    }
  }, [sortConfig, storageKey, currentWorkspace]);

  useEffect(() => {
    if (currentWorkspace?.id) {
      localStorage.setItem(`${storageKey}_visibleColumns`, JSON.stringify(visibleColumns));
    }
  }, [visibleColumns, storageKey, currentWorkspace]);

  const fetchData = async () => {
    if (!currentWorkspace?.id) return;
    try {
      setLoading(true);
      const response = await api.get('track-sprints-detail/', {
        params: { workspace_id: currentWorkspace.id },
      });
      setData(normalizePayload(response.data.payload));
    } catch (error) {
      console.error('Error fetching track sprint data:', handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  // Handle creating a new sprint
  const handleCreateSprint = async (trackId) => {
    if (!trackId || !currentWorkspace?.id) return;
    
    try {
      setCreatingSprint(trackId);
      await api.post('track-sprints/', {
        track_id: trackId,
        workspace_id: currentWorkspace.id,
      });
      
      await fetchData();
    } catch (error) {
      console.error('Error creating sprint:', handleApiError(error));
    } finally {
      setCreatingSprint(null);
    }
  };

  const handleToggleProjectCompleted = async (projectId, nextValue) => {
    if (!projectId || !currentWorkspace?.id) return;
    const prevData = data;

    setData((prev) =>
      prev.map((epic) => ({
        ...epic,
        projects: epic.projects?.map((p) =>
          p.id === projectId ? { ...p, is_completed: nextValue } : p
        ),
      }))
    );

    try {
      await api.patch(`projects/${projectId}/`, {
        workspace: currentWorkspace.id,
        is_completed: nextValue,
      });
      await fetchData();
    } catch (error) {
      console.error('Error updating project status:', handleApiError(error));
      setData(prevData);
    }
  };

  const handleToggleTrackCompleted = async (trackId, nextValue) => {
    if (!trackId || !currentWorkspace?.id) return;
    const prevData = data;

    setData((prev) =>
      prev.map((epic) => ({
        ...epic,
        projects: epic.projects?.map((project) => ({
          ...project,
          tracks: project.tracks?.map((t) =>
            t.id === trackId ? { ...t, is_completed: nextValue } : t
          ),
        })),
      }))
    );

    try {
      await api.patch(`tracks/${trackId}/`, {
        workspace: currentWorkspace.id,
        is_completed: nextValue,
      });
      await fetchData();
    } catch (error) {
      console.error('Error updating track status:', handleApiError(error));
      setData(prevData);
    }
  };

  // Optimistically patch a single project's fields in local state.
  const patchProjectInState = (projectId, updates) => {
    setData((prev) =>
      prev.map((epic) => ({
        ...epic,
        projects: epic.projects?.map((p) =>
          p.id === projectId ? { ...p, ...updates } : p
        ),
      }))
    );
  };

  // Optimistically patch a single track's fields in local state.
  const patchTrackInState = (trackId, updates) => {
    setData((prev) =>
      prev.map((epic) => ({
        ...epic,
        projects: epic.projects?.map((project) => ({
          ...project,
          tracks: project.tracks?.map((t) =>
            t.id === trackId ? { ...t, ...updates } : t
          ),
        })),
      }))
    );
  };

  // Debounced description persistence (avoids a request per keystroke and
  // avoids refetching, which would blow away the focused input).
  const debouncedUpdateProjectDescription = useDebounce(async (projectId, description) => {
    if (!projectId || !currentWorkspace?.id) return;
    try {
      await api.patch(`projects/${projectId}/`, {
        workspace: currentWorkspace.id,
        description,
      });
    } catch (error) {
      console.error('Error updating project description:', handleApiError(error));
    }
  }, 500);

  const debouncedUpdateTrackDescription = useDebounce(async (trackId, description) => {
    if (!trackId || !currentWorkspace?.id) return;
    try {
      await api.patch(`tracks/${trackId}/`, {
        workspace: currentWorkspace.id,
        description,
      });
    } catch (error) {
      console.error('Error updating track description:', handleApiError(error));
    }
  }, 500);

  const handleProjectDescriptionChange = (projectId, description) => {
    patchProjectInState(projectId, { description });
    debouncedUpdateProjectDescription(projectId, description);
  };

  const handleTrackDescriptionChange = (trackId, description) => {
    patchTrackInState(trackId, { description });
    debouncedUpdateTrackDescription(trackId, description);
  };

  const handleProjectOwnerChange = async (projectId, userId) => {
    if (!projectId || !currentWorkspace?.id) return;
    const prevData = data;
    const newOwner = userId
      ? (() => {
          const u = workspaceUsers.find((usr) => usr.id === userId);
          return u ? { id: u.id, name: u.full_name || u.username || u.email } : null;
        })()
      : null;
    patchProjectInState(projectId, { owner: newOwner });

    try {
      await api.patch(`projects/${projectId}/`, {
        workspace: currentWorkspace.id,
        project_manager: userId || null,
      });
    } catch (error) {
      console.error('Error updating project owner:', handleApiError(error));
      setData(prevData);
    }
  };

  const handleTrackOwnerChange = async (trackId, userId) => {
    if (!trackId || !currentWorkspace?.id) return;
    const prevData = data;
    const newOwner = userId
      ? (() => {
          const u = workspaceUsers.find((usr) => usr.id === userId);
          return u ? { id: u.id, name: u.full_name || u.username || u.email } : null;
        })()
      : null;
    patchTrackInState(trackId, { owner: newOwner });

    try {
      await api.patch(`tracks/${trackId}/`, {
        workspace: currentWorkspace.id,
        assignee: userId || null,
      });
    } catch (error) {
      console.error('Error updating track owner:', handleApiError(error));
      setData(prevData);
    }
  };

  const expandAll = () => {
    const epicIds = {};
    const projectIds = {};
    const trackIds = {};

    data.forEach((epic) => {
      epicIds[epic.id] = true;
      epic.projects?.forEach((project) => {
        projectIds[project.id] = true;
        project.tracks?.forEach((track) => {
          trackIds[track.id] = true;
        });
      });
    });

    setExpandedEpics(epicIds);
    setExpandedProjects(projectIds);
    setExpandedTracks(trackIds);
  };

  const collapseAll = () => {
    setExpandedEpics({});
    setExpandedProjects({});
    setExpandedTracks({});
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleColumnsClick = (event) => {
    setColumnsAnchorEl(event.currentTarget);
  };

  const handleColumnsClose = () => {
    setColumnsAnchorEl(null);
  };

  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const handleAddFilter = () => {
    if (newFilter.value.trim()) {
      setFilters((prev) => [...prev, { ...newFilter, id: Date.now() }]);
      setNewFilter({ field: 'epic', operator: 'contains', value: '' });
    }
  };

  const handleRemoveFilter = (filterId) => {
    setFilters((prev) => prev.filter((f) => f.id !== filterId));
  };

  const clearFilters = () => {
    setFilters([]);
  };

  const clearSort = () => {
    setSortConfig({ field: null, direction: 'asc' });
  };

  const toggleEpic = (epicId) => {
    setExpandedEpics((prev) => ({
      ...prev,
      [epicId]: !prev[epicId],
    }));
  };

  const toggleProject = (projectId) => {
    setExpandedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  const toggleTrack = (trackId) => {
    setExpandedTracks((prev) => ({
      ...prev,
      [trackId]: !prev[trackId],
    }));
  };

  // Debounced update for sprint goal
  const debouncedUpdateSprintGoal = useDebounce(async (goalId, updates) => {
    if (!goalId || !currentWorkspace?.id) return;
    try {
      await api.patch(`sprint-goals/${goalId}/`, {
        ...updates,
        workspace: currentWorkspace.id,
      });
      // Refresh data after update
      const response = await api.get('track-sprints-detail/', {
        params: { workspace_id: currentWorkspace.id },
      });
      setData(normalizePayload(response.data.payload));
    } catch (error) {
      console.error('Error updating sprint goal:', handleApiError(error));
    }
  }, 400);

  // Handle goal text change
  const handleGoalChange = (goalId, newText) => {
    // Update local state immediately for responsive UI
    setData((prevData) =>
      prevData.map((epic) => ({
        ...epic,
        projects: epic.projects?.map((project) => ({
          ...project,
          tracks: project.tracks?.map((track) => ({
            ...track,
            sprints: track.sprints?.map((sprint) => ({
              ...sprint,
              sprint_goals: sprint.sprint_goals?.map((goal) =>
                goal.id === goalId ? { ...goal, title: newText } : goal
              ),
            })),
          })),
        })),
      }))
    );
    // Debounce the API call
    debouncedUpdateSprintGoal(goalId, { title: newText });
  };

  // Handle creating a new sprint goal
  const handleCreateGoal = async (sprintId, trackId) => {
    const goalText = newGoalInputs[sprintId];
    if (!goalText?.trim() || !currentWorkspace?.id) return;

    try {
      setCreatingGoal(sprintId);
      await api.post('sprint-goals/', {
        sprint: sprintId,
        track: trackId,
        title: goalText,
        workspace: currentWorkspace.id,
        is_completed: false,
      });
      
      showSnackbar('Sprint goal created successfully', 'success');
      // Clear the input
      setNewGoalInputs((prev) => ({ ...prev, [sprintId]: '' }));
      
      // Refresh data
      const response = await api.get('track-sprints-detail/', {
        params: { workspace_id: currentWorkspace.id },
      });
      setData(normalizePayload(response.data.payload));
    } catch (error) {
      console.error('Error creating sprint goal:', handleApiError(error));
      showSnackbar('Error creating sprint goal', 'error');
      setCreatingGoal(null);
    }
  };

  // Handle status change
  const handleStatusChange = async (goalId, currentStatus) => {
    if (!currentWorkspace?.id) return;
    
    const newStatus = !currentStatus;
    
    // Update local state immediately
    setData((prevData) =>
      prevData.map((epic) => ({
        ...epic,
        projects: epic.projects?.map((project) => ({
          ...project,
          tracks: project.tracks?.map((track) => ({
            ...track,
            sprints: track.sprints?.map((sprint) => ({
              ...sprint,
              sprint_goals: sprint.sprint_goals?.map((goal) =>
                goal.id === goalId ? { ...goal, is_completed: newStatus } : goal
              ),
            })),
          })),
        })),
      }))
    );

    // Immediately send the API request (no debounce for checkbox)
    try {
      await api.patch(`sprint-goals/${goalId}/`, {
        is_completed: newStatus,
        workspace: currentWorkspace.id,
      });
    } catch (error) {
      console.error('Error updating sprint goal status:', handleApiError(error));
      // Revert on error
      setData((prevData) =>
        prevData.map((epic) => ({
          ...epic,
          projects: epic.projects?.map((project) => ({
            ...project,
            tracks: project.tracks?.map((track) => ({
              ...track,
              sprints: track.sprints?.map((sprint) => ({
                ...sprint,
                sprint_goals: sprint.sprint_goals?.map((goal) =>
                  goal.id === goalId ? { ...goal, is_completed: currentStatus } : goal
                ),
              })),
            })),
          })),
        }))
      );
    }
  };

  const filteredAndSortedData = useMemo(() => {
    let processedData = [...data];

    // Helper function to check if value matches filter
    const matchesFilter = (value, filter) => {
      const lowerValue = String(value).toLowerCase();
      const lowerFilterValue = filter.value.toLowerCase();
      
      if (filter.operator === 'contains') {
        return lowerValue.includes(lowerFilterValue);
      } else if (filter.operator === 'doesNotContain') {
        return !lowerValue.includes(lowerFilterValue);
      }
      return true;
    };

    // Apply filters
    if (filters.length > 0) {
      processedData = processedData
        .map((epic) => {
          const epicFilters = filters.filter((f) => f.field === 'epic');
          if (epicFilters.length > 0 && !epicFilters.every((f) => matchesFilter(epic.title, f))) {
            return null;
          }

          const filteredProjects = epic.projects
            ?.map((project) => {
              const projectFilters = filters.filter((f) => f.field === 'project');
              if (projectFilters.length > 0 && !projectFilters.every((f) => matchesFilter(project.title, f))) {
                return null;
              }

              const filteredTracks = project.tracks
                ?.map((track) => {
                  const trackFilters = filters.filter((f) => f.field === 'track');

                  if (trackFilters.length > 0 && !trackFilters.every((f) => matchesFilter(track.title, f))) {
                    return null;
                  }

                  const filteredSprints = track.sprints
                    ?.map((sprint) => {
                      const sprintFilters = filters.filter((f) => f.field === 'sprint');
                      if (sprintFilters.length > 0 && !sprintFilters.every((f) => matchesFilter(sprint.title, f))) {
                        return null;
                      }

                      const goalFilters = filters.filter((f) => f.field === 'goal');
                      const statusFilters = filters.filter((f) => f.field === 'status');
                      
                      let filteredGoals = sprint.sprint_goals;
                      
                      // Apply goal filters
                      if (goalFilters.length > 0) {
                        filteredGoals = filteredGoals?.filter((goal) =>
                          goalFilters.every((f) => matchesFilter(goal.title, f))
                        );
                      }
                      
                      // Apply status filters
                      if (statusFilters.length > 0) {
                        filteredGoals = filteredGoals?.filter((goal) => {
                          return statusFilters.every((f) => {
                            const isCompleted = goal.is_completed ? 'complete' : 'incomplete';
                            return matchesFilter(isCompleted, f);
                          });
                        });
                      }

                      if ((goalFilters.length > 0 || statusFilters.length > 0) && (!filteredGoals || filteredGoals.length === 0)) {
                        return null;
                      }

                      return { ...sprint, sprint_goals: filteredGoals };
                    })
                    .filter(Boolean);

                  const sprintFilters = filters.filter((f) => f.field === 'sprint');
                  if (sprintFilters.length > 0 && (!filteredSprints || filteredSprints.length === 0)) {
                    return null;
                  }

                  return { ...track, sprints: filteredSprints || track.sprints };
                })
                .filter(Boolean);

              const trackFilters = filters.filter((f) => f.field === 'track');
              if (trackFilters.length > 0 && (!filteredTracks || filteredTracks.length === 0)) {
                return null;
              }

              return { ...project, tracks: filteredTracks || project.tracks };
            })
            .filter(Boolean);

          const projectFilters = filters.filter((f) => f.field === 'project');
          if (projectFilters.length > 0 && (!filteredProjects || filteredProjects.length === 0)) {
            return null;
          }

          return { ...epic, projects: filteredProjects || epic.projects };
        })
        .filter(Boolean);
    }

    // Apply sorting
    if (sortConfig.field) {
      processedData.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.field) {
          case 'epic':
            aValue = a.title;
            bValue = b.title;
            break;
          case 'project':
            aValue = a.projects?.[0]?.title || '';
            bValue = b.projects?.[0]?.title || '';
            break;
          case 'track':
            aValue = a.projects?.[0]?.tracks?.[0]?.title || '';
            bValue = b.projects?.[0]?.tracks?.[0]?.title || '';
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return processedData;
  }, [data, filters, sortConfig]);

  const visibleColumnCount = useMemo(() => {
    return Object.values(visibleColumns).filter(Boolean).length;
  }, [visibleColumns]);

  const renderDescriptionField = (value, onChange) => (
    <TextField
      fullWidth
      multiline
      maxRows={3}
      variant="outlined"
      size="small"
      placeholder="Add description..."
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
    />
  );

  const renderOwnerSelect = (owner, onChange) => (
    <FormControl size="small" fullWidth>
      <Select
        value={owner?.id ?? ''}
        displayEmpty
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        renderValue={(val) => {
          if (!val) {
            return (
              <Typography variant="body2" color="text.secondary">
                Unassigned
              </Typography>
            );
          }
          const u = workspaceUsers.find((usr) => usr.id === val);
          return u ? (u.full_name || u.username || u.email) : (owner?.name || '');
        }}
      >
        <MenuItem value="">
          <em>Unassigned</em>
        </MenuItem>
        {workspaceUsers.map((u) => (
          <MenuItem key={u.id} value={u.id}>
            {u.full_name || u.username || u.email}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderSprints = (sprints, trackKey, trackId) => {
    if (!sprints || sprints.length === 0) return null;

    return sprints.flatMap((sprint, sprintIndex) => {
      const sprintKey = `${trackKey}-sprint-${sprint.id}`;
      const hasGoals = sprint.sprint_goals && sprint.sprint_goals.length > 0;
      const rows = [];

      if (hasGoals) {
        // Sprint rows with goals - one row per goal
        sprint.sprint_goals.forEach((goal, goalIndex) => {
          rows.push(
            <SprintRow key={`${sprintKey}-goal-${goal.id || goalIndex}`}>
              {visibleColumns.epic && <StyledTableCell />}
              {visibleColumns.project && <StyledTableCell />}
              {visibleColumns.track && <StyledTableCell />}
              {visibleColumns.description && <StyledTableCell />}
              {visibleColumns.owner && <StyledTableCell />}
              {visibleColumns.sprint && (
                <StyledTableCell sx={{ pl: 3 }}>
                  {goalIndex === 0 ? fmtRange(sprint.start, sprint.end) : ''}
                </StyledTableCell>
              )}
              {visibleColumns.goals && (
                <StyledTableCell>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={goal.title || ''}
                    onChange={(e) => handleGoalChange(goal.id, e.target.value)}
                    sx={{ minWidth: 200 }}
                  />
                </StyledTableCell>
              )}
              {visibleColumns.status && (
                <StyledTableCell align="center">
                  <Checkbox
                    checked={goal.is_completed || false}
                    onChange={() => handleStatusChange(goal.id, goal.is_completed)}
                    color="primary"
                  />
                </StyledTableCell>
              )}
              {visibleColumns.tasksCompleted && (
                <StyledTableCell align="right">
                  {goalIndex === 0 ? (sprint.tasks_completed ?? '') : ''}
                </StyledTableCell>
              )}
              {visibleColumns.totalTasks && (
                <StyledTableCell align="right">
                  {goalIndex === 0 ? (sprint.total_tasks ?? '') : ''}
                </StyledTableCell>
              )}
              {visibleColumns.timeTaken && (
                <StyledTableCell align="right">
                  {goalIndex === 0 ? (sprint.time_taken ?? '') : ''}
                </StyledTableCell>
              )}
              {visibleColumns.estimatedTime && (
                <StyledTableCell align="right">
                  {goalIndex === 0 ? (sprint.estimated_time ?? '') : ''}
                </StyledTableCell>
              )}
              {visibleColumns.reviewRemaining && (
                <StyledTableCell align="right">
                  {goalIndex === 0 ? (sprint['review-task-count'] ?? '') : ''}
                </StyledTableCell>
              )}
            </SprintRow>
          );
        });
      } else {
        // Sprint row without goals - show add new goal input in the same row
        rows.push(
          <SprintRow key={sprintKey}>
            {visibleColumns.epic && <StyledTableCell />}
            {visibleColumns.project && <StyledTableCell />}
            {visibleColumns.track && <StyledTableCell />}
            {visibleColumns.description && <StyledTableCell />}
            {visibleColumns.owner && <StyledTableCell />}
            {visibleColumns.sprint && <StyledTableCell sx={{ pl: 3 }}>{fmtRange(sprint.start, sprint.end)}</StyledTableCell>}
            {visibleColumns.goals && (
              <StyledTableCell>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Add new goal..."
                  value={newGoalInputs[sprint.id] || ''}
                  onChange={(e) => setNewGoalInputs((prev) => ({ ...prev, [sprint.id]: e.target.value }))}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newGoalInputs[sprint.id]?.trim()) {
                      handleCreateGoal(sprint.id, trackId);
                    }
                  }}
                  sx={{ minWidth: 200 }}
                />
              </StyledTableCell>
            )}
            {visibleColumns.status && (
              <StyledTableCell align="center">
                {creatingGoal === sprint.id ? (
                  <CircularProgress size={24} />
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleCreateGoal(sprint.id, trackId)}
                    disabled={!newGoalInputs[sprint.id]?.trim()}
                  >
                    Add
                  </Button>
                )}
              </StyledTableCell>
            )}
            {visibleColumns.tasksCompleted && <StyledTableCell align="right">{sprint.tasks_completed ?? ''}</StyledTableCell>}
            {visibleColumns.totalTasks && <StyledTableCell align="right">{sprint.total_tasks ?? ''}</StyledTableCell>}
            {visibleColumns.timeTaken && <StyledTableCell align="right">{sprint.time_taken ?? ''}</StyledTableCell>}
            {visibleColumns.estimatedTime && <StyledTableCell align="right">{sprint.estimated_time ?? ''}</StyledTableCell>}
            {visibleColumns.reviewRemaining && <StyledTableCell align="right">{sprint['review-task-count'] ?? ''}</StyledTableCell>}
          </SprintRow>
        );
      }

      return rows;
    });
  };

  const renderTracks = (tracks, projectKey, project, epic) => {
    if (!tracks || tracks.length === 0) return null;

    return tracks.flatMap((track, trackIndex) => {
      const trackKey = `${projectKey}-track-${track.id}`;
      const hasSprints = track.sprints && track.sprints.length > 0;
      const isExpanded = expandedTracks[track.id];

      const trackRow = (
        <TrackRow key={trackKey}>
          {visibleColumns.epic && <StyledTableCell />}
          {visibleColumns.project && <StyledTableCell />}
          {visibleColumns.track && (
            <ClickableCell sx={{ pl: 3 }} onClick={() => toggleTrack(track.id)}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {hasSprints && (
                  <IconButton size="small" sx={{ mr: 1, p: 0 }}>
                    {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                  </IconButton>
                )}
                {track.title}
                <IconButton
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTaskPrefill({
                      epic: epic?.id,
                      project: project?.id,
                      track: track.id,
                    });
                    setOpenTaskDialog(true);
                  }}
                  aria-label="Add Task"
                >
                  <Add fontSize="small" />
                </IconButton>
              </Box>
            </ClickableCell>
          )}
          {visibleColumns.description && (
            <StyledTableCell>
              {renderDescriptionField(track.description, (value) =>
                handleTrackDescriptionChange(track.id, value)
              )}
            </StyledTableCell>
          )}
          {visibleColumns.owner && (
            <StyledTableCell>
              {renderOwnerSelect(track.owner, (userId) =>
                handleTrackOwnerChange(track.id, userId)
              )}
            </StyledTableCell>
          )}
          {visibleColumns.sprint && (
            <StyledTableCell align="center">
              {creatingSprint === track.id ? (
                <CircularProgress size={20} />
              ) : (
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateSprint(track.id);
                  }}
                  aria-label="Add Sprint"
                  startIcon={<Add />}
                  variant="contained"
                >
                  Sprint
                </Button>
              )}
            </StyledTableCell>
          )}
          {visibleColumns.goals && <StyledTableCell />}
          {visibleColumns.status && (
            <StyledTableCell align="center">
              <Checkbox
                checked={!!track.is_completed}
                onChange={(e) => {
                  e.stopPropagation();
                  handleToggleTrackCompleted(track.id, e.target.checked);
                }}
                color="primary"
              />
            </StyledTableCell>
          )}
          {visibleColumns.tasksCompleted && <StyledTableCell />}
          {visibleColumns.totalTasks && <StyledTableCell />}
          {visibleColumns.timeTaken && <StyledTableCell />}
          {visibleColumns.estimatedTime && <StyledTableCell />}
          {visibleColumns.reviewRemaining && <StyledTableCell />}
        </TrackRow>
      );

      const sprintRows = hasSprints && isExpanded ? renderSprints(track.sprints, trackKey, track.id) : [];

      return [trackRow, ...sprintRows];
    });
  };

  const renderProjects = (projects, epicKey, epic) => {
    if (!projects || projects.length === 0) return null;

    return projects.flatMap((project, projectIndex) => {
      const projectKey = `${epicKey}-project-${project.id}`;
      const hasTracks = project.tracks && project.tracks.length > 0;
      const isExpanded = expandedProjects[project.id];

      const projectRow = (
        <ProjectRow key={projectKey}>
          {visibleColumns.epic && <StyledTableCell />}
          {visibleColumns.project && (
            <ClickableCell sx={{ pl: 2 }} onClick={() => toggleProject(project.id)}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {hasTracks && (
                  <IconButton size="small" sx={{ mr: 1, p: 0 }}>
                    {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                  </IconButton>
                )}
                {project.title}
              </Box>
            </ClickableCell>
          )}
          {visibleColumns.track && <StyledTableCell />}
          {visibleColumns.description && (
            <StyledTableCell>
              {renderDescriptionField(project.description, (value) =>
                handleProjectDescriptionChange(project.id, value)
              )}
            </StyledTableCell>
          )}
          {visibleColumns.owner && (
            <StyledTableCell>
              {renderOwnerSelect(project.owner, (userId) =>
                handleProjectOwnerChange(project.id, userId)
              )}
            </StyledTableCell>
          )}
          {visibleColumns.sprint && <StyledTableCell />}
          {visibleColumns.goals && <StyledTableCell />}
          {visibleColumns.status && (
            <StyledTableCell align="center">
              <Checkbox
                checked={!!project.is_completed}
                onChange={(e) => {
                  e.stopPropagation();
                  handleToggleProjectCompleted(project.id, e.target.checked);
                }}
                color="primary"
              />
            </StyledTableCell>
          )}
          {visibleColumns.tasksCompleted && <StyledTableCell />}
          {visibleColumns.totalTasks && <StyledTableCell />}
          {visibleColumns.timeTaken && <StyledTableCell />}
          {visibleColumns.estimatedTime && <StyledTableCell />}
          {visibleColumns.reviewRemaining && <StyledTableCell />}
        </ProjectRow>
      );

      const trackRows = hasTracks && isExpanded ? renderTracks(project.tracks, projectKey, project, epic) : [];

      return [projectRow, ...trackRows];
    });
  };

  const renderEpics = () => {
    return filteredAndSortedData.flatMap((epic, epicIndex) => {
      const epicKey = `epic-${epic.id}`;
      const hasProjects = epic.projects && epic.projects.length > 0;
      const isExpanded = expandedEpics[epic.id];

      const epicRow = (
        <EpicRow key={epicKey}>
          {visibleColumns.epic && (
            <ClickableCell sx={{ fontWeight: 600 }} onClick={() => toggleEpic(epic.id)}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {hasProjects && (
                  <IconButton size="small" sx={{ mr: 1, p: 0 }}>
                    {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                  </IconButton>
                )}
                {epic.title}
              </Box>
            </ClickableCell>
          )}
          {visibleColumns.project && <StyledTableCell />}
          {visibleColumns.track && <StyledTableCell />}
          {visibleColumns.description && <StyledTableCell />}
          {visibleColumns.owner && <StyledTableCell />}
          {visibleColumns.sprint && <StyledTableCell />}
          {visibleColumns.goals && <StyledTableCell />}
          {visibleColumns.status && <StyledTableCell />}
          {visibleColumns.tasksCompleted && <StyledTableCell />}
          {visibleColumns.totalTasks && <StyledTableCell />}
          {visibleColumns.timeTaken && <StyledTableCell />}
          {visibleColumns.estimatedTime && <StyledTableCell />}
          {visibleColumns.reviewRemaining && <StyledTableCell />}
        </EpicRow>
      );

      const projectRows = hasProjects && isExpanded ? renderProjects(epic.projects, epicKey, epic) : [];

      return [epicRow, ...projectRows];
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box />
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenProjectDialog(true)}
          >
            Add Project
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setSelectedProject(null);
              setOpenTrackDialog(true);
            }}
          >
            Add Track
          </Button>
          <Button
            variant="contained"
            onClick={expandAll}
          >
            Expand All
          </Button>
          <Button
            variant="contained"
            onClick={collapseAll}
          >
            Collapse All
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={handleFilterClick}
          >
            Filter ({filters.length})
          </Button>
          <Button
            variant="outlined"
            startIcon={<Sort />}
            onClick={handleSortClick}
          >
            Sort {sortConfig.field && `(${sortConfig.field})`}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ViewColumn />}
            onClick={handleColumnsClick}
          >
            Columns
          </Button>
        </Stack>
      </Stack>

      {/* Active Filters */}
      {filters.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" sx={{ mr: 1 }}>
              Active Filters:
            </Typography>
            {filters.map((filter) => (
              <Chip
                key={filter.id}
                label={`${filter.field} ${filter.operator === 'contains' ? 'contains' : "doesn't contain"} "${filter.value}"`}
                size="small"
                onDelete={() => handleRemoveFilter(filter.id)}
              />
            ))}
            <Button
              size="small"
              startIcon={<Clear />}
              onClick={clearFilters}
            >
              Clear All
            </Button>
          </Stack>
        </Box>
      )}

      {/* Sort indicator */}
      {sortConfig.field && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ mr: 1 }}>
              Sorted by:
            </Typography>
            <Chip
              label={`${sortConfig.field} (${sortConfig.direction})`}
              size="small"
              onDelete={clearSort}
            />
          </Stack>
        </Box>
      )}

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{ sx: { minWidth: 400 } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Add Filter
          </Typography>
          <Stack spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Field</InputLabel>
              <Select
                value={newFilter.field}
                label="Field"
                onChange={(e) => setNewFilter({ ...newFilter, field: e.target.value })}
              >
                <MenuItem value="epic">Epic</MenuItem>
                <MenuItem value="project">Project</MenuItem>
                <MenuItem value="track">Track</MenuItem>
                <MenuItem value="sprint">Sprint</MenuItem>
                <MenuItem value="goal">Goal</MenuItem>
                <MenuItem value="status">Status</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" fullWidth>
              <InputLabel>Operator</InputLabel>
              <Select
                value={newFilter.operator}
                label="Operator"
                onChange={(e) => setNewFilter({ ...newFilter, operator: e.target.value })}
              >
                <MenuItem value="contains">Contains</MenuItem>
                <MenuItem value="doesNotContain">Doesn't Contain</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              size="small"
              label="Value"
              fullWidth
              value={newFilter.value}
              onChange={(e) => setNewFilter({ ...newFilter, value: e.target.value })}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddFilter();
                }
              }}
            />
            
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddFilter}
              disabled={!newFilter.value.trim()}
              fullWidth
            >
              Add Filter
            </Button>
          </Stack>
          
          {filters.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Current Filters ({filters.length})
              </Typography>
              <Stack spacing={1}>
                {filters.map((filter) => (
                  <Box
                    key={filter.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2">
                      {filter.field} {filter.operator === 'contains' ? 'contains' : "doesn't contain"} "{filter.value}"
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFilter(filter.id)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </Box>
      </Menu>

      {/* Sort Menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleSortClose}
        PaperProps={{ sx: { minWidth: 300 } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Sort By
          </Typography>
          <Stack spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Field</InputLabel>
              <Select
                value={sortConfig.field || ''}
                label="Field"
                onChange={(e) => setSortConfig({ ...sortConfig, field: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="epic">Epic</MenuItem>
                <MenuItem value="project">Project</MenuItem>
                <MenuItem value="track">Track</MenuItem>
              </Select>
            </FormControl>
            
            {sortConfig.field && (
              <ToggleButtonGroup
                value={sortConfig.direction}
                exclusive
                onChange={(e, newDirection) => {
                  if (newDirection) {
                    setSortConfig({ ...sortConfig, direction: newDirection });
                  }
                }}
                fullWidth
                size="small"
              >
                <ToggleButton value="asc">
                  Ascending
                </ToggleButton>
                <ToggleButton value="desc">
                  Descending
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            
            {sortConfig.field && (
              <Button
                variant="outlined"
                startIcon={<Clear />}
                onClick={clearSort}
                fullWidth
              >
                Clear Sort
              </Button>
            )}
          </Stack>
        </Box>
      </Menu>

      {/* Columns Menu */}
      <Menu
        anchorEl={columnsAnchorEl}
        open={Boolean(columnsAnchorEl)}
        onClose={handleColumnsClose}
        PaperProps={{ sx: { minWidth: 300 } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Show/Hide Columns
          </Typography>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={visibleColumns.epic}
                onChange={() => toggleColumnVisibility('epic')}
                size="small"
              />
              <Typography variant="body2">EPICs</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={visibleColumns.project}
                onChange={() => toggleColumnVisibility('project')}
                size="small"
              />
              <Typography variant="body2">Project</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={visibleColumns.track}
                onChange={() => toggleColumnVisibility('track')}
                size="small"
              />
              <Typography variant="body2">Track</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={visibleColumns.description}
                onChange={() => toggleColumnVisibility('description')}
                size="small"
              />
              <Typography variant="body2">Description</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={visibleColumns.owner}
                onChange={() => toggleColumnVisibility('owner')}
                size="small"
              />
              <Typography variant="body2">Owner</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={visibleColumns.sprint}
                onChange={() => toggleColumnVisibility('sprint')}
                size="small"
              />
              <Typography variant="body2">Sprint</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={visibleColumns.goals}
                onChange={() => toggleColumnVisibility('goals')}
                size="small"
              />
              <Typography variant="body2">Goals</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={visibleColumns.status}
                onChange={() => toggleColumnVisibility('status')}
                size="small"
              />
              <Typography variant="body2">Status</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={visibleColumns.tasksCompleted}
                onChange={() => toggleColumnVisibility('tasksCompleted')}
                size="small"
              />
              <Typography variant="body2">Tasks Completed</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={visibleColumns.totalTasks}
                onChange={() => toggleColumnVisibility('totalTasks')}
                size="small"
              />
              <Typography variant="body2">Total Tasks</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={visibleColumns.timeTaken}
                onChange={() => toggleColumnVisibility('timeTaken')}
                size="small"
              />
              <Typography variant="body2">Time Taken</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={visibleColumns.estimatedTime}
                onChange={() => toggleColumnVisibility('estimatedTime')}
                size="small"
              />
              <Typography variant="body2">Estimated Time</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={visibleColumns.reviewRemaining}
                onChange={() => toggleColumnVisibility('reviewRemaining')}
                size="small"
              />
              <Typography variant="body2">Review Remaining</Typography>
            </Box>
          </Stack>
        </Box>
      </Menu>

      <StyledPaper elevation={1}>
        <TableContainer sx={{ overflowX: 'auto' }}>
        <Table sx={{ tableLayout: 'fixed', width: 'max-content' }}>
          <TableHead>

            <TableRow>
              {visibleColumns.epic && <StyledTableHeadCell sx={COL_SX.epic}>EPICs</StyledTableHeadCell>}
              {visibleColumns.project && <StyledTableHeadCell sx={COL_SX.project}>Project</StyledTableHeadCell>}
              {visibleColumns.track && <StyledTableHeadCell sx={COL_SX.track}>Track</StyledTableHeadCell>}
              {visibleColumns.description && <StyledTableHeadCell sx={COL_SX.description}>Description</StyledTableHeadCell>}
              {visibleColumns.owner && <StyledTableHeadCell sx={COL_SX.owner}>Owner</StyledTableHeadCell>}
              {visibleColumns.sprint && <StyledTableHeadCell sx={COL_SX.sprint}>Sprint</StyledTableHeadCell>}
              {visibleColumns.goals && <StyledTableHeadCell sx={COL_SX.goals}>Goals</StyledTableHeadCell>}
              {visibleColumns.status && <StyledTableHeadCell sx={COL_SX.status}>Status</StyledTableHeadCell>}
              {visibleColumns.tasksCompleted && <StyledTableHeadCell sx={COL_SX.metric}>Tasks Completed</StyledTableHeadCell>}
              {visibleColumns.totalTasks && <StyledTableHeadCell sx={COL_SX.metric}>Total Tasks</StyledTableHeadCell>}
              {visibleColumns.timeTaken && <StyledTableHeadCell sx={COL_SX.metric}>Time Taken</StyledTableHeadCell>}
              {visibleColumns.estimatedTime && <StyledTableHeadCell sx={COL_SX.metric}>Estimated Time</StyledTableHeadCell>}
              {visibleColumns.reviewRemaining && <StyledTableHeadCell sx={COL_SX.metric}>Review Remaining</StyledTableHeadCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedData.length > 0 ? (
              renderEpics()
            ) : (
              <TableRow>
                <StyledTableCell colSpan={visibleColumnCount} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  {data.length === 0 ? 'No data available' : 'No results match the current filters'}
                </StyledTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </StyledPaper>

      <CreateProjectDialog
        open={openProjectDialog}
        onClose={() => setOpenProjectDialog(false)}
        onSuccess={() => {
          setOpenProjectDialog(false);
          fetchData();
        }}
      />

      <CreateTrackDialog
        projectId={selectedProject}
        open={openTrackDialog}
        onClose={() => setOpenTrackDialog(false)}
        onSuccess={() => {
          setOpenTrackDialog(false);
          fetchData();
        }}
      />

      <TaskCreate
        open={openTaskDialog}
        prefill={taskPrefill}
        onClose={() => setOpenTaskDialog(false)}
        onTaskCreated={() => {
          setOpenTaskDialog(false);
          fetchData();
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TrackSprintDetail;
